import { z } from "zod";

import { fetchWithError, useParsedQueries, useParsedQuery } from "@nens/react-query-utils";
import { combineUrlAndParams } from "@nens/shared-utils";

import { multiPolygonSchema, polygonSchema } from "../geojson";
import { paginatedSchema } from "../pagination";

import { baseAssetWithTimeseriesSchema } from "./assetsBase";

export const pumpedDrainageAreaListItemSchema = baseAssetWithTimeseriesSchema.extend({
  geometry: polygonSchema.or(multiPolygonSchema),
  name: z.string(),
  sewer_system: z.string().nullable(),
  upstream_load: z.number().nullable(),
  connected_impervious_surface: z.number().nullable(),
  connected_impervious_surface_mixed: z.number().nullable(),
  connected_impervious_surface_rainwater: z.number().nullable(),
  population_equivalent: z.number().nullable(),
  pollution_equivalent: z.number().nullable(),
  water_consumption: z.number().nullable(),
  extraneous_water: z.number().nullable(),
  sanitary_load: z.number().nullable(),
  lowest_invert_level: z.number().nullable(),
  water_retention_capacity: z.number().nullable(),
  minimum_overflow_crest_level: z.number().nullable(),
  downstream_pumped_drainage_area: z.string().nullable() /* URL to Pumped Drainage Area */,
  pump_station: z.string().nullable() /* URL to Pump Station */,
  area_type: z.string(),
  inhabitants: z.number().nullable(),
  pump_overcapacity: z.number().nullable(),
});

export type PumpedDrainageAreaListItem = z.infer<typeof pumpedDrainageAreaListItemSchema>;

export const pumpedDrainageAreaListSchema = paginatedSchema(pumpedDrainageAreaListItemSchema);
export type PumpedDrainageAreaList = z.infer<typeof pumpedDrainageAreaListSchema>;

export const pumpedDrainageAreaSchema = pumpedDrainageAreaListItemSchema;
export type PumpedDrainageArea = z.infer<typeof pumpedDrainageAreaSchema>;

export function usePumpedDrainageAreas(params = {}) {
  const url = combineUrlAndParams("/api/v4/pumpeddrainageareas/", params);
  return useParsedQuery<PumpedDrainageAreaList>(
    ["lizard-api-v4", "pumpeddrainageareas", url],
    () => fetchWithError(url),
    (json: unknown) => pumpedDrainageAreaListSchema.parse(json)
  );
}

export function usePumpedDrainageArea(id: number) {
  return useParsedQuery<PumpedDrainageArea>(
    ["lizard-api-v4", "pumpeddrainageareas", id],
    () => fetchWithError(`/api/v4/pumpeddrainageareas/${id}/`),
    (json: unknown) => pumpedDrainageAreaSchema.parse(json)
  );
}

export function useMultiplePumpedDrainageAreas(ids: number[]) {
  return useParsedQueries(
    ids.map((id) => {
      return {
        queryKey: ["lizard-api-v4", "pumpeddrainageareas", id],
        fetchFunction: () => fetchWithError(`/api/v4/pumpeddrainageareas/${id}/`),
        parseFunction: (json: unknown) => pumpedDrainageAreaSchema.parse(json),
      };
    })
  );
}
